import { apiClient } from "./client";

/**
 * API:et serialiserar enums som strängar (JsonStringEnumConverter), inte som siffror.
 * Vid PATCH accepterar System.Text.Json båda, men allt som *läses* är strängar.
 */
export type PosterStatus = "Active" | "TakenDown" | "Vandalized";

export interface PosterFeatureProperties {
    id: string;
    quantity: number;
    status: PosterStatus;
    placedByMe: boolean;
    placedAt: string;
}

export interface PostersGeoJson {
    type: "FeatureCollection";
    features: Array<{
        type: "Feature";
        id: string;
        geometry: { type: "Point"; coordinates: [number, number] };
        properties: PosterFeatureProperties;
    }>;
    metadata: { totalCount: number };
}

export interface PosterStatistics {
    myTodayCount: number;
    teamTodayCount: number;
    /** Affischer som fortfarande sitter uppe – nedtagna räknas inte in. */
    teamTotalCount: number;
    teamPlacedTotalCount: number;
    teamTakenDownCount: number;
    teamVandalizedCount: number;
}

export interface PlacePosterRequest {
    campaignId: string;
    latitude: number;
    longitude: number;
    quantity: number;
}

/**
 * Kartans bbox är strängen "west,south,east,north", men affisch-endpointen binder
 * de fyra hörnen som separata query-parametrar. Skickas de som en enda bbox-sträng
 * blir alla fyra 0 och svaret tomt – därför splittas den här.
 */
const bboxParams = (bbox?: string): Record<string, number> | undefined => {
    if (!bbox) return undefined;
    const parts = bbox.split(",").map((value) => Number.parseFloat(value));
    if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) return undefined;
    const [west, south, east, north] = parts;
    return { west, south, east, north };
};

/**
 * Utan `campaignId` returneras affischerna från alla kampanjer användaren har åtkomst
 * till. Affischer hör hemma på kartan oavsett vilken kampanj de sattes upp i.
 */
export const fetchPostersGeoJson = async (
    campaignId: string | null | undefined,
    bbox?: string,
    signal?: AbortSignal
): Promise<PostersGeoJson> => {
    const response = await apiClient.get<PostersGeoJson>("/map/posters", {
        params: { ...(campaignId ? { campaignId } : {}), ...bboxParams(bbox) },
        signal
    });
    return response.data;
};

export const placePoster = async (request: PlacePosterRequest): Promise<{ id: string }> => {
    const response = await apiClient.post<{ id: string }>("/posters", request);
    return response.data;
};

export const deletePoster = async (posterId: string): Promise<void> => {
    await apiClient.delete(`/posters/${posterId}`);
};

export const updatePosterStatus = async (posterId: string, status: PosterStatus): Promise<void> => {
    await apiClient.patch(`/posters/${posterId}/status`, { status });
};

export const getPosterStatistics = async (
    campaignId?: string | null
): Promise<PosterStatistics> => {
    const response = await apiClient.get<PosterStatistics>("/posters/statistics", {
        params: campaignId ? { campaignId } : {}
    });
    return response.data;
};

// --- Admin ---

export interface PosterAdminFilter {
    campaignId?: string;
    /** ISO-datum (yyyy-MM-dd), inklusive. */
    from?: string;
    /** ISO-datum (yyyy-MM-dd), inklusive. */
    to?: string;
}

export interface PosterDailyCount {
    date: string;
    placedQuantity: number;
    placedEntries: number;
    takenDownQuantity: number;
    vandalizedQuantity: number;
}

export interface PosterUserCount {
    userId: string;
    userName: string;
    placedQuantity: number;
    placedEntries: number;
    activeQuantity: number;
    takenDownQuantity: number;
    vandalizedQuantity: number;
    lastPlacedAt: string | null;
}

export interface PosterCampaignCount {
    campaignId: string;
    campaignName: string;
    placedQuantity: number;
    activeQuantity: number;
    takenDownQuantity: number;
    vandalizedQuantity: number;
}

export interface PosterStatisticsOverview {
    placedQuantity: number;
    placedEntries: number;
    activeQuantity: number;
    takenDownQuantity: number;
    vandalizedQuantity: number;
    policeReportedEntries: number;
    repairedEntries: number;
    distinctPlacers: number;
    firstDate: string | null;
    lastDate: string | null;
}

export interface PosterAdminStatistics {
    overview: PosterStatisticsOverview;
    daily: PosterDailyCount[];
    byUser: PosterUserCount[];
    byCampaign: PosterCampaignCount[];
}

export interface VandalizedPosterRow {
    id: string;
    campaignId: string;
    campaignName: string;
    latitude: number;
    longitude: number;
    addressLabel: string | null;
    quantity: number;
    status: PosterStatus;
    placedById: string;
    placedByName: string;
    placedAt: string;
    vandalizedAt: string | null;
    vandalizedByName: string | null;
    policeReportedAt: string | null;
    policeReportNumber: string | null;
    takenDownAt: string | null;
    repairedAt: string | null;
    vandalismNotes: string | null;
}

export interface UpdatePosterVandalismRequest {
    policeReported?: boolean;
    policeReportNumber?: string;
    takenDown?: boolean;
    repaired?: boolean;
    vandalismNotes?: string;
}

export const getPosterAdminStatistics = async (
    filter: PosterAdminFilter
): Promise<PosterAdminStatistics> => {
    const response = await apiClient.get<PosterAdminStatistics>("/posters/admin/statistics", {
        params: filter
    });
    return response.data;
};

export const getVandalizedPosters = async (
    filter: PosterAdminFilter
): Promise<VandalizedPosterRow[]> => {
    const response = await apiClient.get<VandalizedPosterRow[]>("/posters/admin/vandalized", {
        params: filter
    });
    return response.data;
};

/** Ångrar en felaktig vandaliseringsmarkering. Affischen finns kvar på kartan. */
export const clearPosterVandalism = async (posterId: string): Promise<void> => {
    await apiClient.delete(`/posters/${posterId}/vandalism`);
};

export const updatePosterVandalism = async (
    posterId: string,
    request: UpdatePosterVandalismRequest
): Promise<VandalizedPosterRow> => {
    const response = await apiClient.patch<VandalizedPosterRow>(
        `/posters/${posterId}/vandalism`,
        request
    );
    return response.data;
};
